import React from 'react';
import { useTranslation } from 'react-i18next';
import './RecipeCard.css';
import { sanitizeUrl } from '../../utils/functions';
import AuthenticatedImage from '../AuthenticatedImage/AuthenticatedImage';

/**
 * Reusable recipe card used on the Foods & Recipes page and anywhere
 * else a compact recipe preview is needed.
 *
 * @param {Object}   props
 * @param {string}   props.name     - Recipe title.
 * @param {string}   props.image    - Image URL.
 * @param {string}   props.time     - Preparation time label (e.g. "15 min").
 * @param {number}   props.kcal     - Total calories.
 * @param {number}   props.servings - Number of servings.
 * @param {number}   props.protein  - Grams of protein.
 * @param {number}   props.carbs    - Grams of carbohydrates.
 * @param {number}   props.fat      - Grams of fat.
 * @param {Function} [props.onClick] - Optional click handler for the whole card.
 * @param {Function} [props.onDelete] - Optional click handler for recipe delete.
 */
const RecipeCard = ({ name, image, time, kcal, servings, protein, carbs, fat, onClick, onDelete }) => {
    const { t } = useTranslation();

    return (
        <div className="rc-card" onClick={onClick}>
            {/* ── Image + time badge ───────────────────────────── */}
            <div className="rc-img-wrapper">
                <AuthenticatedImage src={sanitizeUrl(image)} fallbackSrc='/images/recipe_image_placeholder.jpg' alt={name} />
                {time && <div className="rc-badge">{time.toUpperCase()}</div>}
                {onDelete && (
                    <button
                        className="rc-delete-btn"
                        onClick={(e) => {
                            e.stopPropagation();
                            onDelete();
                        }}
                        title={t('far-delete-recipe')}
                        aria-label={t('far-delete-recipe')}
                    >
                        <span className="material-symbols-outlined">delete</span>
                    </button>
                )}
            </div>

            {/* ── Body ─────────────────────────────────────────── */}
            <div className="rc-body">
                <h4>{name}</h4>

                {/* Meta: calories & servings */}
                <div className="rc-meta">
                    <span>
                        <span className="material-symbols-outlined">local_fire_department</span>
                        {kcal} kcal
                    </span>
                    <span>
                        <span className="material-symbols-outlined">person</span>
                        {servings} {t('far-serving')}
                    </span>
                </div>

                {/* Macro pills */}
                <div className="rc-macros">
                    <div className="rc-macro-pill highlight">
                        <span className="rc-macro-label">P</span>
                        <span className="rc-macro-value">{protein}g</span>
                    </div>
                    <div className="rc-macro-pill default">
                        <span className="rc-macro-label">C</span>
                        <span className="rc-macro-value">{carbs}g</span>
                    </div>
                    <div className="rc-macro-pill default">
                        <span className="rc-macro-label">F</span>
                        <span className="rc-macro-value">{fat}g</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RecipeCard;
